import { isFunction } from 'lodash';

import { Tooltip } from '@/components/tooltip';
import { ScheduleChartRange } from '@/types';
import { tv } from '@/utils/styles';

import { BarTooltip } from './bar-tooltip';
import { calcPercentOfMax, ConfigObjInterface } from './utils';

interface Props extends ScheduleChartRange, ConfigObjInterface {
  maxHeight: number;
  value: number;
  allowDateSearch?: boolean;
  onClick?: (date: Date) => void;
}

const Bar = ({
  date,
  dateLabel,
  monthLabel,
  dayLabel,
  isStartOfMonth,
  isToday,
  isInFuture,
  maxHeight,
  showDayDateLabel,
  tooltipFreqLabel,
  value,
  allowDateSearch,
  onClick,
}: Props) => {
  const barDate = new Date(date);

  const handleBarClick = () => {
    if (isFunction(onClick)) {
      onClick(barDate);
    }
  };

  const { base, bar, label, monthLabelStyles, monthLabelDivider, dayIndicator } = styles({
    isPredicted: isInFuture,
    hasValue: value > 0,
    isToday,
    showDayDateLabel,
  });

  return (
    <Tooltip
      content={
        <BarTooltip
          dateLabel={dateLabel}
          frequency={value}
          frequencyLabel={tooltipFreqLabel}
          showSearchPrompt={allowDateSearch && isInFuture}
        />
      }
      followCursor="horizontal"
    >
      <div onClick={handleBarClick} className={base()} role="button" onKeyDown={handleBarClick} tabIndex={-1}>
        <div
          className={bar({ className: 'bar' })}
          style={{
            height: `${calcPercentOfMax(value, maxHeight)}%`,
          }}
        />
        {showDayDateLabel && <div className={label()}>{dayLabel}</div>}
        {isStartOfMonth && (
          <div className={monthLabelStyles()}>
            {monthLabel}
            <span className={monthLabelDivider()} />
          </div>
        )}
        {isToday && <span className={dayIndicator()} />}
      </div>
    </Tooltip>
  );
};

const styles = tv({
  slots: {
    base: 'group relative flex h-full cursor-pointer items-end rounded-full hover:bg-grey-100',
    bar: 'relative min-h-0 w-full rounded-full group-hover:!bg-lightBlue-700 group-hover:!bg-none',
    dayIndicator: 'absolute -top-2 left-1/2 -ml-1 border-4 border-transparent border-t-error',
    label: 'absolute -bottom-5 left-0 right-0 text-center text-xxs uppercase leading-[14px] text-grey-500',
    monthLabelStyles: 'absolute -bottom-6 left-1 text-xxs uppercase text-grey-500',
    monthLabelDivider: 'absolute -left-2 bottom-1 h-5 w-px bg-grey-300',
  },
  variants: {
    isToday: {
      true: {
        dayIndicator: 'block',
        label: 'font-semibold text-text-primary',
      },
      false: null,
    },
    showDayDateLabel: {
      true: {
        monthLabelStyles: '!-bottom-[36px]',
        monthLabelDivider: 'h-[26px]',
      },
      false: null,
    },
    hasValue: {
      true: null,
      false: null,
    },
    isPredicted: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    {
      isPredicted: false,
      hasValue: true,
      className: '[&_.bar]:!bg-grey-300',
    },
    {
      isPredicted: true,
      hasValue: true,
      isToday: false,
      className: '[&_.bar]:bg-lightBlue-400 [&_.bar]:shadow-md',
    },
    {
      isPredicted: true,
      hasValue: true,
      isToday: true,
      className: '[&_.bar]:border-2 [&_.bar]:border-lightBlue-800 [&_.bar]:bg-lightBlue-600 [&_.bar]:shadow-md',
    },
  ],
});

export { Bar };
